import React, { useState } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import './MembershipPage.css';

const FormSection = ({ title, children }) => (
  <div className="mp-section">
    <div className="mp-section-subhead">
      <span className="mp-section-title">{title}</span>
    </div>
    {children}
  </div>
);

const FormField = ({ label, placeholder, type = "text", value = "", onChange = () => {} }) => (
  <div className="mp-field">
    {label && <label className="mp-field-label">{label}</label>}
    <div className="mp-input">
      <div className="mp-input-contents">
        <input 
          className="mp-field-text" 
          type={type} 
          placeholder={placeholder || ''} 
          value={value}
          onChange={onChange}
        />
      </div>
      <div className="mp-input-line" />
    </div>
  </div>
);

const MembershipPage = ({ onNavigate }) => {
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    fullNames: ['', '', '', '', ''],
    addressLines: ['', ''],
    familyNameIndia: '',
    permanentMemberNumber: '',
    sndpRegistrationNumber: '',
    unitName: '',
    unitNumber: '',
    unitAddress: '',
    branchName: '',
    branchNumber: '',
    administrativePositionUK: '',
    administrativePositionIndia: '',
    legalIssues: ['', ''],
    fatherName: '',
    motherName: '',
    suggestions: ['', '', ''],
    eventNotificationEmail: '',
    date: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayInputChange = (field, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Structure data to match backend's expected format
      const submissionData = {
        email: formData.eventNotificationEmail,
        familyMembers: formData.fullNames,
        ukAddress: {
          line1: formData.addressLines[0] || '',
          line2: formData.addressLines[1] || ''
        },
        familyNameIndia: formData.familyNameIndia,
        permanentMemberNumber: formData.permanentMemberNumber,
        sndpRegistrationNumber: formData.sndpRegistrationNumber,
        unitDetails: {
          name: formData.unitName,
          number: formData.unitNumber,
          address: formData.unitAddress
        },
        branchIndia: {
          name: formData.branchName,
          number: formData.branchNumber
        },
        administrativePositions: {
          uk: formData.administrativePositionUK,
          india: formData.administrativePositionIndia
        },
        legalIssues: formData.legalIssues,
        parents: {
          fatherName: formData.fatherName,
          motherName: formData.motherName
        },
        suggestions: formData.suggestions,
        date: formData.date
      };

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('data', JSON.stringify(submissionData));
      if (photoFile) {
        formDataToSend.append('photo', photoFile);
      }

      // Send to backend API
      const response = await fetch('/api/membership/submit', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        alert('Membership application submitted successfully! Event notifications will be sent to: ' + formData.eventNotificationEmail);
        // Reset form
        setFormData({
          fullNames: ['', '', '', '', ''],
          addressLines: ['', ''],
          familyNameIndia: '',
          permanentMemberNumber: '',
          sndpRegistrationNumber: '',
          unitName: '',
          unitNumber: '',
          unitAddress: '',
          branchName: '',
          branchNumber: '',
          administrativePositionUK: '',
          administrativePositionIndia: '',
          legalIssues: ['', ''],
          fatherName: '',
          motherName: '',
          suggestions: ['', '', ''],
          eventNotificationEmail: '',
          date: '',
        });
        setPhotoFile(null);
        setPhotoPreview(null);
      } else {
        alert('Error submitting form. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting form: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mp-page">
      <Navbar onNavigate={onNavigate} light />

      <div className="mp-container">
        {/* Logo & Organisation Name */}
        <div className="mp-org-header">
          <img src="/Sndp/logo.png" alt="Pravasi SNDP Yogam Logo" className="mp-org-logo" />
          <p className="mp-org-name">
            PRAVASI SNDP YOGAM UK<br />
            പ്രവാസി എസ്.എൻ.ഡി.പി യോഗം യു.കെ
          </p>
        </div>

        {/* Page Title */}
        <div className="mp-title-block">
          <h2 className="mp-title">
            Membership Application Form | അംഗത്വ അപേക്ഷ ഫോം
          </h2>
          <div className="mp-title-bar" />
        </div>

        <form className="mp-form" onSubmit={handleSubmit}>

          {/* Section 1 – Family Members */}
          <FormSection title="1. Name & Address of Family Members (Age) | കുടുംബാംഗങ്ങളുടെ പേര്, വിലാസം (പ്രായം)">
            <div className="mp-field">
              <label className="mp-field-label">Full Name / പൂർണ്ണ നാമം</label>
              {formData.fullNames.map((name, i) => (
                <div key={i} className="mp-input">
                  <div className="mp-input-contents">
                    <input 
                      className="mp-field-text" 
                      type="text" 
                      placeholder={`Member ${i + 1}`}
                      value={name}
                      onChange={(e) => handleArrayInputChange('fullNames', i, e.target.value)}
                    />
                  </div>
                  <div className="mp-input-line" />
                </div>
              ))}
            </div>

            <div className="mp-field">
              <label className="mp-field-label">Address / വിലാസം (UK)</label>
              {formData.addressLines.map((address, i) => (
                <div key={i} className="mp-input">
                  <div className="mp-input-contents">
                    <input 
                      className="mp-field-text" 
                      type="text" 
                      placeholder={`Address Line ${i + 1}`}
                      value={address}
                      onChange={(e) => handleArrayInputChange('addressLines', i, e.target.value)}
                    />
                  </div>
                  <div className="mp-input-line" />
                </div>
              ))}
            </div>
          </FormSection>

          {/* Section 2 – Family Name in India */}
          <FormSection title="2. Family Name in India | ഇന്ത്യയിലെ കുടുംബപ്പേര്">
            <FormField 
              label="Family Name / കുടുംബപ്പേര്" 
              placeholder="Enter family name"
              value={formData.familyNameIndia}
              onChange={(e) => handleInputChange('familyNameIndia', e.target.value)}
            />
          </FormSection>

          {/* Section 3 – Permanent Member Number */}
          <FormSection title="3. Permanent Member(s) Number (UK) | സ്ഥിരാംഗ നമ്പർ (യു.കെ)">
            <FormField 
              label="Member Number / അംഗ നമ്പർ" 
              placeholder="Enter member number"
              value={formData.permanentMemberNumber}
              onChange={(e) => handleInputChange('permanentMemberNumber', e.target.value)}
            />
          </FormSection>

          {/* Section 4 – SNDP Registration */}
          <FormSection title="4. SNDP Yogam India Registration No | എസ്.എൻ.ഡി.പി യോഗം ഇന്ത്യ രജിസ്ട്രേഷൻ നമ്പർ">
            <FormField 
              label="Registration Number / രജിസ്ട്രേഷൻ നമ്പർ" 
              placeholder="Enter registration number"
              value={formData.sndpRegistrationNumber}
              onChange={(e) => handleInputChange('sndpRegistrationNumber', e.target.value)}
            />
          </FormSection>

          {/* Section 5 – Unit Details */}
          <FormSection title="5. Unit Details | യൂണിറ്റ് വിശദാംശങ്ങൾ">
            <FormField 
              label="Unit Name / യൂണിറ്റ് പേര്" 
              placeholder="Enter unit name"
              value={formData.unitName}
              onChange={(e) => handleInputChange('unitName', e.target.value)}
            />
            <FormField 
              label="Unit Number / യൂണിറ്റ് നമ്പർ" 
              placeholder="Enter unit number"
              value={formData.unitNumber}
              onChange={(e) => handleInputChange('unitNumber', e.target.value)}
            />
            <FormField 
              label="Unit Address / യൂണിറ്റ് വിലാസം" 
              placeholder="Enter unit address"
              value={formData.unitAddress}
              onChange={(e) => handleInputChange('unitAddress', e.target.value)}
            />
          </FormSection>

          {/* Section 6 – Branch in India */}
          <FormSection title="6. Branch in India | ഇന്ത്യയിലെ ശാഖ">
            <FormField 
              label="Branch Name / ശാഖ പേര്" 
              placeholder="Enter branch name"
              value={formData.branchName}
              onChange={(e) => handleInputChange('branchName', e.target.value)}
            />
            <FormField 
              label="Branch Number / ശാഖ നമ്പർ" 
              placeholder="Enter branch number"
              value={formData.branchNumber}
              onChange={(e) => handleInputChange('branchNumber', e.target.value)}
            />
          </FormSection>

          {/* Section 7 – Administrative Position */}
          <FormSection title="7. Administrative Position Held ( if any ) | ഭരണസ്ഥാനം (ഉണ്ടെങ്കിൽ)">
            <FormField 
              placeholder="Position held in UK"
              value={formData.administrativePositionUK}
              onChange={(e) => handleInputChange('administrativePositionUK', e.target.value)}
            />
            <FormField 
              placeholder="Position held in India"
              value={formData.administrativePositionIndia}
              onChange={(e) => handleInputChange('administrativePositionIndia', e.target.value)}
            />
          </FormSection>

          {/* Section 8 – Legal Issues */}
          <FormSection title="8. Legal Issues (if any) | നിയമപ്രശ്നങ്ങൾ (ഉണ്ടെങ്കിൽ)">
            {formData.legalIssues.map((issue, i) => (
              <FormField 
                key={i}
                placeholder={`Legal issue ${i + 1} (if any)`}
                value={issue}
                onChange={(e) => handleArrayInputChange('legalIssues', i, e.target.value)}
              />
            ))}
          </FormSection>

          {/* Section 9 – Parents / Guardian */}
          <FormSection title="9. Parents / Guardian Details | രക്ഷിതാക്കളുടെ വിവരം">
            <div className="mp-field">
              <div className="mp-input">
                <div className="mp-input-contents">
                  <input 
                    className="mp-field-text" 
                    type="text" 
                    placeholder="Father's Name / പിതാവിന്റെ പേര്"
                    value={formData.fatherName}
                    onChange={(e) => handleInputChange('fatherName', e.target.value)}
                  />
                </div>
                <div className="mp-input-line" />
              </div>
              <div className="mp-input">
                <div className="mp-input-contents">
                  <input 
                    className="mp-field-text" 
                    type="text" 
                    placeholder="Mother's Name / മാതാവിന്റെ പേര്"
                    value={formData.motherName}
                    onChange={(e) => handleInputChange('motherName', e.target.value)}
                  />
                </div>
                <div className="mp-input-line" />
              </div>
            </div>
          </FormSection>

          {/* Section 10 – Event Notification Email */}
          <FormSection title="10. Email for Event Notifications | ഇവന്റ് അറിയിപ്പുകൾക്കുള്ള ഇമെയിൽ">
            <FormField 
              label="Email Address / ഇമെയിൽ വിലാസം" 
              placeholder="Enter email address for event notifications"
              type="email"
              value={formData.eventNotificationEmail}
              onChange={(e) => handleInputChange('eventNotificationEmail', e.target.value)}
            />
          </FormSection>

          {/* Section 11 – Suggestions */}
          <FormSection title="11. Suggestions | നിർദ്ദേശങ്ങൾ">
            {formData.suggestions.map((suggestion, i) => (
              <FormField 
                key={i}
                placeholder={`Suggestion ${i + 1}`}
                value={suggestion}
                onChange={(e) => handleArrayInputChange('suggestions', i, e.target.value)}
              />
            ))}
          </FormSection>

          {/* Section 12 – Declaration */}
          <div className="mp-section mp-declaration-section">
            <div className="mp-section-subhead">
              <span className="mp-section-title mp-section-title--bold">Declaration | പ്രഖ്യാപനം</span>
            </div>

            <p className="mp-declaration-text">
              I declare that the above information is true and correct. I agree to abide by the rules of Pravasi SNDP Yogam UK.<br />
              മുകളിൽ നൽകിയ വിവരങ്ങൾ ശരിയാണെന്ന് ഞാൻ പ്രഖ്യാപിക്കുന്നു. യോഗത്തിന്റെ നിയമങ്ങൾ പാലിക്കുമെന്ന് സമ്മതിക്കുന്നു.
            </p>

            <div className="mp-declaration-row">
              {/* Date field */}
              <div className="mp-field mp-field--half">
                <label className="mp-field-label">Date / തീയതി</label>
                <div className="mp-input">
                  <div className="mp-input-contents mp-select-contents">
                    <input 
                      className="mp-field-text" 
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                    />
                    <div className="mp-select-icon" />
                  </div>
                  <div className="mp-input-line" />
                </div>
              </div>

              {/* Photo upload */}
              <div className="mp-field mp-field--half">
                <label className="mp-field-label">Photo / ഫോട്ടോ</label>
                <div className="mp-upload-row">
                  <label className="mp-browse-btn">
                    Browse...
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      style={{ display: 'none' }}
                      onChange={handlePhotoChange}
                    />
                  </label>
                  {photoFile && (
                    <span className="mp-filename">{photoFile.name}</span>
                  )}
                </div>
                <span className="mp-upload-hint">( Supports PNG, JPEG, and JPG formats only )</span>
                {photoPreview && (
                  <div className="mp-preview-row">
                    <img src={photoPreview} alt="Preview" className="mp-preview-img" />
                    <button type="button" className="mp-delete-btn" onClick={removePhoto} aria-label="Remove photo">
                      🗑
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" className="mp-submit-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>

        </form>
      </div>

      <Footer />
    </div>
  );
};

export default MembershipPage;
